import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared IP-camera plumbing for the live views.
 *
 * A camera is configured once on /safety/zones (IP + login + auto-detected stream
 * path). These helpers let the other live pages *use* that camera: list the ones
 * with a real feed, poll a live still, and drop a "watch an IP camera" source into
 * a page that was webcam-only. The connection card itself lives here too so a
 * camera can be added or fixed without leaving the page.
 */

export interface LiveCamera {
  id: number;
  name: string;
  source: string;
  location: string | null;
  referenceKey: string | null;
  customerId: number | null;
  customerName: string | null;
  kinds: string[];
  privacyBlur: boolean | null;
  /** How many zones are drawn on this camera (present from /api/safety/cameras). */
  zoneCount?: number;
}

/** A source is a real feed (not a manual-upload placeholder) when it has a URL scheme. */
export function isLiveSource(source: string): boolean {
  return /^(rtsp|https?):\/\//i.test(source);
}

/**
 * The safety API a screen talks to. The admin shell hits `/api/safety` (all
 * cameras); the tenant portal hits `/api/portal/safety` (its own, RLS-scoped).
 * Every shared component takes this so the SAME UI serves both — that is the
 * whole point: one camera-entry experience, two audiences.
 */
export const DEFAULT_API_BASE = '/api/safety';

/** The snapshot URL for a camera — a single live JPEG, cache-busted per call. */
export function snapshotUrl(id: number, ts?: number, apiBase: string = DEFAULT_API_BASE): string {
  return `${apiBase}/cameras/${id}/snapshot?ts=${ts ?? 0}`;
}

/** Fetch one live frame as a Blob (for handing to detection / inspection). */
export async function fetchSnapshotBlob(id: number, apiBase: string = DEFAULT_API_BASE): Promise<Blob> {
  const res = await fetch(snapshotUrl(id, Date.now(), apiBase), { cache: 'no-store' });
  if (!res.ok) {
    const msg = await res
      .json()
      .then((j) => (j as { message?: string }).message)
      .catch(() => '');
    throw new Error(msg || `Could not grab a frame (${res.status})`);
  }
  return res.blob();
}

/** Load the safety cameras once, exposing only the ones with a live feed by default. */
export function useLiveCameras(
  liveOnly = true,
  apiBase: string = DEFAULT_API_BASE,
): { cameras: LiveCamera[]; reload: () => void } {
  const [cameras, setCameras] = useState<LiveCamera[]>([]);
  const reload = useCallback(() => {
    fetch(`${apiBase}/cameras`)
      .then((r) => (r.ok ? r.json() : []))
      .then((cs: LiveCamera[]) => setCameras(liveOnly ? cs.filter((c) => isLiveSource(c.source)) : cs))
      .catch(() => undefined);
  }, [liveOnly, apiBase]);
  useEffect(() => reload(), [reload]);
  return { cameras, reload };
}

// ── Connection helpers (shared with SafetyZonesView) ────────────────────────

export interface Conn {
  scheme: 'rtsp' | 'http' | 'https';
  host: string;
  user: string;
  pass: string;
  path: string;
}
/** Decode a URL-encoded credential part, tolerating a raw (unencoded) value. */
function dec(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}
export function parseSource(src: string): Conn {
  if (!src || src === 'upload') return { scheme: 'rtsp', host: '', user: '', pass: '', path: '' };
  const m = /^(\w+):\/\/(?:([^:@/]+)(?::([^@/]*))?@)?([^/]+)(\/.*)?$/.exec(src.trim());
  if (!m) return { scheme: 'rtsp', host: src.trim(), user: '', pass: '', path: '' };
  const scheme = (m[1] === 'http' || m[1] === 'https' ? m[1] : 'rtsp') as Conn['scheme'];
  return { scheme, user: dec(m[2] ?? ''), pass: dec(m[3] ?? ''), host: m[4] ?? '', path: m[5] ?? '' };
}
export function buildSource(c: Conn): string {
  if (!c.host.trim()) return 'upload';
  // Encode credentials — a password with !*%@:/ would otherwise corrupt the URL.
  const cred = c.user ? `${encodeURIComponent(c.user)}${c.pass ? `:${encodeURIComponent(c.pass)}` : ''}@` : '';
  return `${c.scheme}://${cred}${c.host.trim()}${c.path}`;
}
export function previewSource(c: Conn): string {
  return buildSource({ ...c, pass: c.pass ? '••••' : '' });
}

export const SOURCE_PRESETS: Array<{ label: string; scheme: Conn['scheme']; path: string }> = [
  { label: 'Generic RTSP (root)', scheme: 'rtsp', path: '/' },
  { label: 'Hipcam / generic — main (/11)', scheme: 'rtsp', path: '/11' },
  { label: 'Hipcam / generic — sub (/12)', scheme: 'rtsp', path: '/12' },
  { label: 'Hikvision', scheme: 'rtsp', path: '/Streaming/Channels/101' },
  { label: 'Dahua / Amcrest', scheme: 'rtsp', path: '/cam/realmonitor?channel=1&subtype=0' },
  { label: 'Axis', scheme: 'rtsp', path: '/axis-media/media.amp' },
  { label: 'Reolink', scheme: 'rtsp', path: '/h264Preview_01_main' },
  { label: 'Foscam', scheme: 'rtsp', path: '/videoMain' },
  { label: 'HTTP snapshot (JPEG)', scheme: 'http', path: '/snapshot.jpg' },
];

const FIELD: React.CSSProperties = { display: 'grid', gap: 4, fontSize: 13, margin: 0 };

/**
 * Add a camera — one prompt for the name; the IP/login is set on the connection
 * card afterwards. Shared so every "＋ Add" acts identically wherever it appears.
 */
export async function addSafetyCamera(apiBase: string = DEFAULT_API_BASE): Promise<number | null> {
  const name = prompt('Name this camera (e.g. "Loading bay — east corner")');
  if (!name) return null;
  // Starts on manual upload; the live feed is set on the connection card. On the
  // admin base it lands unassigned; on the portal base it is owned by the caller's
  // tenant (the server takes the owner from the session, never from here).
  const res = await fetch(`${apiBase}/cameras`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name, source: 'upload' }),
  });
  if (!res.ok) throw new Error('Could not add camera');
  const { id } = (await res.json()) as { id: number };
  return id;
}

/**
 * The camera dropdown — one component so the picker looks and behaves the same on
 * every screen (admin zones/live, and the tenant portal). A live source shows a
 * filled dot, a manual-upload one a hollow dot; the last option always adds a
 * camera. `liveOnly` narrows it to cameras with a real feed; `placeholder` adds a
 * leading empty option (for pages where "none" is a valid state).
 */
/** The minimal camera shape the dropdown needs — so admin and portal rows both fit. */
export interface PickableCamera {
  id: number;
  name: string;
  source: string;
  customerName?: string | null;
  zoneCount?: number;
}

export function CameraPicker({
  cameras,
  value,
  onChange,
  onAdd,
  liveOnly = false,
  placeholder,
  style,
}: {
  cameras: PickableCamera[];
  value: number | null;
  onChange: (id: number | null) => void;
  onAdd: () => void;
  liveOnly?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
}) {
  const list = liveOnly ? cameras.filter((c) => isLiveSource(c.source)) : cameras;
  return (
    <select
      value={value ?? ''}
      style={{ minWidth: 260, ...style }}
      onChange={(e) => {
        if (e.target.value === '__add__') onAdd();
        else onChange(e.target.value ? Number(e.target.value) : null);
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {list.length === 0 && !placeholder && <option value="">No cameras yet</option>}
      {list.map((c) => {
        const bits: string[] = [c.name];
        if (c.customerName !== undefined) bits.push(c.customerName ?? 'unassigned');
        if (c.zoneCount !== undefined) bits.push(`${c.zoneCount} zone${c.zoneCount === 1 ? '' : 's'}`);
        return (
          <option key={c.id} value={c.id}>
            {isLiveSource(c.source) ? '● ' : '○ '}
            {bits.join(' · ')}
          </option>
        );
      })}
      <option value="__add__">＋ Add an IP camera…</option>
    </select>
  );
}

/** The connection to a live camera — IP/host, brand path, and separate credentials. */
export function CameraConnection({
  camera,
  onChanged,
  onNotice,
  onError,
  apiBase = DEFAULT_API_BASE,
}: {
  camera: { id: number; source: string };
  onChanged: () => void;
  onNotice: (m: string) => void;
  onError: (m: string) => void;
  apiBase?: string;
}) {
  const [c, setC] = useState<Conn>(() => parseSource(camera.source));
  const [busy, setBusy] = useState(false);
  const [showPass, setShowPass] = useState(false);
  useEffect(() => setC(parseSource(camera.source)), [camera.id, camera.source]);

  const built = buildSource(c);
  const isLive = built !== 'upload';
  const dirty = built !== camera.source;

  const saveSource = async (source: string) => {
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/cameras/${camera.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ source }),
      });
      if (!res.ok) throw new Error('Could not save the connection');
      onNotice(source === 'upload' ? 'Set to manual upload.' : 'Camera connection saved.');
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const testGrab = async () => {
    if (dirty) await saveSource(built);
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/cameras/${camera.id}/grab-reference`, { method: 'POST' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not reach the camera');
      onNotice('Connected — grabbed a frame from the feed.');
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const autodetect = async () => {
    if (!c.host.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/cameras/${camera.id}/autodetect`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ host: c.host.trim(), user: c.user, pass: c.pass }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'No stream detected on that IP');
      const d = (await res.json()) as { path?: string };
      onNotice(`Found the stream${d.path ? ` (${d.path})` : ''} and grabbed a frame.`);
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const blurSave = () => {
    if (dirty) void saveSource(built);
  };

  return (
    <div className="grid" style={{ gap: 10 }}>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
        <label style={{ ...FIELD, flex: '2 1 210px' }}>
          Camera IP / host
          <input value={c.host} placeholder="192.168.1.29" onChange={(e) => setC({ ...c, host: e.target.value })} onBlur={blurSave} />
        </label>
        <label style={{ ...FIELD, flex: '2 1 200px' }}>
          Brand / stream path
          <select
            value={c.path}
            onChange={(e) => {
              const p = SOURCE_PRESETS.find((x) => x.path === e.target.value);
              const next = p ? { ...c, scheme: p.scheme, path: p.path } : c;
              setC(next);
              if (next.host && buildSource(next) !== camera.source) void saveSource(buildSource(next));
            }}
          >
            {!SOURCE_PRESETS.some((p) => p.path === c.path) && <option value={c.path}>Custom ({c.path || '—'})</option>}
            {SOURCE_PRESETS.map((p) => (
              <option key={p.label} value={p.path}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
        <label style={{ ...FIELD, flex: '1 1 140px' }}>
          Username
          <input value={c.user} placeholder="admin" autoComplete="off" onChange={(e) => setC({ ...c, user: e.target.value })} onBlur={blurSave} />
        </label>
        <label style={{ ...FIELD, flex: '1 1 140px' }}>
          Password
          <input
            type={showPass ? 'text' : 'password'}
            value={c.pass}
            placeholder="••••••"
            autoComplete="new-password"
            onChange={(e) => setC({ ...c, pass: e.target.value })}
            onBlur={blurSave}
          />
        </label>
        <label className="row" style={{ gap: 6, margin: '0 0 8px', alignItems: 'center', fontSize: 12 }}>
          <input type="checkbox" checked={showPass} onChange={(e) => setShowPass(e.target.checked)} /> show
        </label>
        <div style={{ flex: 1 }} />
        <button
          className="btn sm primary"
          disabled={busy || !c.host.trim()}
          onClick={() => void autodetect()}
          title="Don't know the path? Try the common streams for this IP and use the one that works"
          style={{ marginBottom: 2 }}
        >
          {busy ? 'Detecting…' : 'Auto-detect stream'}
        </button>
        <button className="btn sm" disabled={busy || !isLive} onClick={() => void testGrab()} title="Connect with the path you set and pull a still" style={{ marginBottom: 2 }}>
          Test &amp; grab
        </button>
      </div>
      <div className="row" style={{ gap: 8, alignItems: 'center', fontSize: 12 }}>
        <span className="health-dot" style={{ width: 10, height: 10, borderRadius: '50%', background: isLive ? 'var(--good)' : 'var(--text-dim)' }} />
        {isLive ? (
          <span className="muted">
            Live source · <code style={{ fontSize: 12 }}>{previewSource(c)}</code>
          </span>
        ) : (
          <span className="muted">Manual upload — enter the camera IP above to monitor 24/7.</span>
        )}
        {isLive && (
          <button
            className="btn sm"
            disabled={busy}
            onClick={() => {
              setC(parseSource('upload'));
              void saveSource('upload');
            }}
          >
            Clear
          </button>
        )}
      </div>
      <div className="muted" style={{ fontSize: 11 }}>
        Tip: some cameras silently trim long/special-character passwords — if a good password is rejected, try it without the last symbols, or set a simpler one on the camera.
      </div>
    </div>
  );
}

/**
 * Pick a configured IP camera as a live source. Shows the feed (polled snapshots),
 * lets you add/fix a camera inline, and hands each frame to the parent. Used on
 * /live (one-shot inspect) and /safety/live (continuous detection).
 */
export function IpCameraSource({
  cameraId,
  onPick,
  intervalMs = 1500,
  showConnect = false,
  hidePreview = false,
  apiBase = DEFAULT_API_BASE,
  zonesLink = '/safety/zones',
}: {
  cameraId: number | null;
  onPick: (id: number | null) => void;
  intervalMs?: number;
  showConnect?: boolean;
  /** Suppress the built-in still preview when the parent already shows the feed. */
  hidePreview?: boolean;
  /** Which safety API to talk to — admin (default) or the tenant portal. */
  apiBase?: string;
  /** Where "Cameras & zones →" points; null hides it. */
  zonesLink?: string | null;
}) {
  const { cameras, reload } = useLiveCameras(false, apiBase);
  const [connectFor, setConnectFor] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selected = cameras.find((c) => c.id === cameraId) ?? null;

  const addCamera = async () => {
    try {
      const id = await addSafetyCamera(apiBase);
      if (id == null) return;
      reload();
      setConnectFor(id);
      onPick(id);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="grid" style={{ gap: 8 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <CameraPicker
          cameras={cameras}
          value={cameraId}
          onChange={onPick}
          onAdd={() => void addCamera()}
          liveOnly
          placeholder="Choose an IP camera…"
          style={{ minWidth: 200, fontSize: 13 }}
        />
        {selected && showConnect && (
          <button className="btn sm" onClick={() => setConnectFor((v) => (v === selected.id ? null : selected.id))}>
            {connectFor === selected.id ? 'Hide connection' : '⚙ Connection'}
          </button>
        )}
        {zonesLink && (
          <Link to={zonesLink} style={{ fontSize: 12 }}>
            Cameras &amp; zones →
          </Link>
        )}
      </div>

      {notice && <div className="panel" style={{ color: 'var(--good)', fontSize: 12 }}>{notice}</div>}
      {error && <div className="panel" style={{ color: 'var(--bad)', fontSize: 12 }}>{error}</div>}

      {connectFor != null && (
        <div className="panel" style={{ padding: 12 }}>
          <CameraConnection
            camera={cameras.find((c) => c.id === connectFor) ?? { id: connectFor, source: 'upload' }}
            apiBase={apiBase}
            onChanged={() => {
              reload();
              setNotice('Camera connection updated.');
            }}
            onNotice={setNotice}
            onError={setError}
          />
        </div>
      )}

      {selected && isLiveSource(selected.source) && !hidePreview && (
        <LivePreview cameraId={selected.id} intervalMs={intervalMs} apiBase={apiBase} />
      )}
    </div>
  );
}

/** A small refreshing still of a camera — a lightweight "is it live?" preview. */
function LivePreview({ cameraId, intervalMs, apiBase }: { cameraId: number; intervalMs: number; apiBase: string }) {
  const [ts, setTs] = useState(0);
  const [ok, setOk] = useState<boolean | null>(null);
  useEffect(() => {
    setOk(null);
    const id = setInterval(() => setTs(Date.now()), Math.max(800, intervalMs));
    setTs(Date.now());
    return () => clearInterval(id);
  }, [cameraId, intervalMs]);
  return (
    <div className="panel" style={{ padding: 6, lineHeight: 0, display: 'inline-block', maxWidth: 320 }}>
      <img
        src={snapshotUrl(cameraId, ts, apiBase)}
        alt="camera preview"
        style={{ maxWidth: '100%', borderRadius: 4, display: 'block' }}
        onLoad={() => setOk(true)}
        onError={() => setOk(false)}
      />
      {ok === false && (
        <div className="muted" style={{ fontSize: 11, padding: 6, lineHeight: 1.4 }}>
          No frame yet — check the connection (⚙) or that the camera is reachable.
        </div>
      )}
    </div>
  );
}

// ── Zones — the shared editor and its types (admin + tenant draw identically) ──

export type ZoneRule = 'ppe' | 'no-entry' | 'occupancy' | 'all-humans' | 'no-vehicles';
export interface ZonePoint {
  x: number;
  y: number;
}
export interface Zone {
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Free-form area (normalised points). Null = the x/y/w/h rectangle (legacy). */
  polygon: ZonePoint[] | null;
  rule: ZoneRule;
  requireVest: boolean;
  requireHelmet: boolean;
  maxOccupancy: number | null;
}
export const RULE_COLOUR: Record<ZoneRule, string> = {
  ppe: 'var(--warn)',
  'no-entry': 'var(--bad)',
  occupancy: 'var(--teal)',
  'all-humans': 'var(--accent)',
  'no-vehicles': 'var(--violet)',
};
export const RULE_LABEL: Record<ZoneRule, string> = {
  ppe: 'PPE required',
  'no-entry': 'No entry (people)',
  occupancy: 'Occupancy cap',
  'all-humans': 'All humans',
  'no-vehicles': 'No vehicles',
};
/** The polygon points for a zone — its own, or the rectangle turned into four. */
export function zonePoly(z: Zone): ZonePoint[] {
  if (z.polygon && z.polygon.length >= 3) return z.polygon;
  return [
    { x: z.x, y: z.y },
    { x: z.x + z.w, y: z.y },
    { x: z.x + z.w, y: z.y + z.h },
    { x: z.x, y: z.y + z.h },
  ];
}
/** Bounding box of a set of normalised points. */
export function bboxOf(pts: ZonePoint[]): { x: number; y: number; w: number; h: number } {
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}
/** Ray-cast point-in-polygon on normalised (0..1) coordinates. */
export function pointInPoly(px: number, py: number, poly: ZonePoint[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i]!.x;
    const yi = poly[i]!.y;
    const xj = poly[j]!.x;
    const yj = poly[j]!.y;
    const hit = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}

/**
 * Draw free-form zones on a camera still and attach a rule to each. Image-agnostic:
 * the parent supplies `imageUrl` (a stored reference on the admin side, a frozen
 * live snapshot on the tenant side) and, optionally, a `replace` action (upload a
 * new still, or grab a fresh frame). Loads and saves via `apiBase`, so the same
 * editor serves staff and tenants against their own scoped API.
 */
export function ZoneEditor({
  cameraId,
  imageUrl,
  apiBase = DEFAULT_API_BASE,
  onSaved,
  onError,
  replace,
  defaultRule,
}: {
  cameraId: number;
  imageUrl: string;
  apiBase?: string;
  /** Rule a freshly drawn zone starts on — the wizard presets this per scenario. */
  defaultRule?: ZoneRule;
  onSaved: (n: number) => void;
  onError: (m: string) => void;
  replace?: { label: string; onClick: () => void };
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [zones, setZones] = useState<Zone[]>([]);
  const [pts, setPts] = useState<ZonePoint[]>([]); // the polygon being drawn
  const [drawing, setDrawing] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setZones([]);
    setSelected(null);
    fetch(`${apiBase}/cameras/${cameraId}/zones`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setZones)
      .catch(() => undefined);
  }, [cameraId, apiBase]);

  const toNorm = (cx: number, cy: number) => {
    const r = imgRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (cx - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (cy - r.top) / r.height)),
    };
  };
  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/cameras/${cameraId}/zones`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(zones),
      });
      if (!res.ok) throw new Error('Save failed');
      onSaved(zones.length);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const finish = () => {
    if (pts.length >= 3) {
      const bb = bboxOf(pts);
      setZones((z) => [
        ...z,
        { name: `Zone ${z.length + 1}`, ...bb, polygon: pts, rule: defaultRule ?? 'no-entry', requireVest: false, requireHelmet: false, maxOccupancy: null },
      ]);
      setSelected(zones.length);
    }
    setPts([]);
    setDrawing(false);
  };
  const addPoint = (e: React.MouseEvent) => {
    if (!drawing || !imageUrl || !imgRef.current) return;
    const p = toNorm(e.clientX, e.clientY);
    if (pts.length >= 3) {
      const f = pts[0]!;
      if (Math.hypot(p.x - f.x, p.y - f.y) < 0.025) return void finish();
    }
    setPts((cur) => [...cur, p]);
  };
  const cancelDraw = () => {
    setPts([]);
    setDrawing(false);
  };

  return (
    <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 16 }}>
      <div style={{ position: 'relative', alignSelf: 'start', background: '#000', borderRadius: 8, userSelect: 'none' }}>
        {imageUrl ? (
          <img ref={imgRef} src={imageUrl} alt="camera reference" style={{ display: 'block', width: '100%', borderRadius: 8 }} draggable={false} />
        ) : (
          <div style={{ height: 340, display: 'grid', placeItems: 'center', color: 'var(--text-dim)' }}>
            No frame yet — connect the camera (or grab/upload a still) to draw zones.
          </div>
        )}
        {imageUrl && (
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: drawing ? 'crosshair' : 'default' }}
            onClick={addPoint}
            onDoubleClick={(e) => {
              e.preventDefault();
              finish();
            }}
          >
            {zones.map((zn, i) => {
              const colour = RULE_COLOUR[zn.rule];
              const poly = zonePoly(zn)
                .map((p) => `${p.x * 100},${p.y * 100}`)
                .join(' ');
              return (
                <polygon
                  key={i}
                  points={poly}
                  fill={`color-mix(in srgb, ${colour} 22%, transparent)`}
                  stroke={colour}
                  strokeWidth={i === selected ? 2.5 : 2}
                  strokeDasharray={i === selected ? '5 4' : undefined}
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                  style={{ cursor: drawing ? 'crosshair' : 'pointer' }}
                  onClick={(e) => {
                    if (drawing) return;
                    e.stopPropagation();
                    setSelected(i === selected ? null : i);
                  }}
                />
              );
            })}
            {pts.length > 0 && (
              <polyline
                points={pts.map((p) => `${p.x * 100},${p.y * 100}`).join(' ')}
                fill="none"
                stroke="var(--accent)"
                strokeWidth={2}
                strokeDasharray="5 4"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
        )}
        {pts.map((p, k) => (
          <span
            key={k}
            style={{ position: 'absolute', left: `${p.x * 100}%`, top: `${p.y * 100}%`, width: 10, height: 10, marginLeft: -5, marginTop: -5, borderRadius: '50%', background: k === 0 ? 'var(--good)' : 'var(--accent)', boxShadow: '0 0 0 2px rgba(0,0,0,.6)', pointerEvents: 'none' }}
          />
        ))}
        {zones.map((zn, i) => {
          const bb = bboxOf(zonePoly(zn));
          return (
            <span
              key={i}
              style={{ position: 'absolute', left: `${bb.x * 100}%`, top: `calc(${bb.y * 100}% - 20px)`, background: RULE_COLOUR[zn.rule], color: '#fff', fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap', pointerEvents: 'none' }}
            >
              {zn.name} · {RULE_LABEL[zn.rule]}
            </span>
          );
        })}
      </div>

      <div className="grid" style={{ gap: 8, alignContent: 'start' }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {replace && (
            <button className="btn sm" disabled={busy} onClick={replace.onClick}>
              {replace.label}
            </button>
          )}
          <button className="btn sm primary" disabled={busy || !imageUrl} onClick={() => void save()}>
            Save zones
          </button>
        </div>
        {imageUrl && (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {!drawing ? (
              <button
                className="btn sm"
                onClick={() => {
                  setDrawing(true);
                  setSelected(null);
                }}
              >
                ＋ Draw zone
              </button>
            ) : (
              <>
                <button className="btn sm primary" disabled={pts.length < 3} onClick={finish}>
                  Finish ({pts.length})
                </button>
                <button className="btn sm" onClick={cancelDraw}>
                  Cancel
                </button>
              </>
            )}
          </div>
        )}
        {imageUrl && (
          <span className="muted" style={{ fontSize: 13 }}>
            {drawing
              ? 'Click to drop points; click the first point again (or Finish / double-click) to close the area.'
              : zones.length === 0
                ? 'Click "Draw zone", then click points on the frame to outline a free-form area.'
                : 'Click "Draw zone" to add another, or a zone below to set its rule.'}
          </span>
        )}
        {zones.map((zn, i) => (
          <div
            key={i}
            className="panel"
            style={{ padding: 10, borderLeft: `3px solid ${RULE_COLOUR[zn.rule]}`, background: i === selected ? 'var(--panel-2)' : undefined }}
            onClick={() => setSelected(i)}
          >
            <div className="row" style={{ gap: 8 }}>
              <input value={zn.name} onChange={(e) => setZones((z) => z.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)))} style={{ flex: 1 }} />
              <button
                className="btn sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setZones((z) => z.filter((_, k) => k !== i));
                  setSelected(null);
                }}
              >
                ✕
              </button>
            </div>
            <select value={zn.rule} onChange={(e) => setZones((z) => z.map((x, k) => (k === i ? { ...x, rule: e.target.value as ZoneRule } : x)))} style={{ width: '100%', marginTop: 8 }}>
              <option value="no-entry">No entry — any person (alarm)</option>
              <option value="ppe">PPE required (helmet / vest)</option>
              <option value="all-humans">All humans — detect anyone present</option>
              <option value="no-vehicles">No vehicles — cars / forklifts (alarm)</option>
              <option value="occupancy">Occupancy cap</option>
            </select>
            {zn.rule === 'ppe' && (
              <div className="row" style={{ gap: 12, marginTop: 8, fontSize: 13 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={zn.requireVest} onChange={(e) => setZones((z) => z.map((x, k) => (k === i ? { ...x, requireVest: e.target.checked } : x)))} /> Hi-vis vest
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={zn.requireHelmet} onChange={(e) => setZones((z) => z.map((x, k) => (k === i ? { ...x, requireHelmet: e.target.checked } : x)))} /> Helmet
                </label>
              </div>
            )}
            {zn.rule === 'occupancy' && (
              <label style={{ display: 'block', marginTop: 8, fontSize: 13 }}>
                Max people
                <input type="number" min={0} value={zn.maxOccupancy ?? ''} onChange={(e) => setZones((z) => z.map((x, k) => (k === i ? { ...x, maxOccupancy: e.target.value ? Number(e.target.value) : null } : x)))} style={{ width: '100%', marginTop: 4 }} />
              </label>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Watch a camera live with its zones drawn on top — the tenant's "monitor" view.
 *
 * Deliberately a watch-only eye: it refreshes the live still and overlays the
 * zones, but raises nothing itself. The 24/7 server monitor is what evaluates the
 * feed and files the screengrab alarm (scoped to the camera's tenant), so this
 * never double-counts. Reads zones through `apiBase`, so it serves staff and
 * tenants against their own scoped API.
 */
export function LiveZonesView({
  cameraId,
  apiBase = DEFAULT_API_BASE,
  intervalMs = 2000,
}: {
  cameraId: number;
  apiBase?: string;
  intervalMs?: number;
}) {
  const [ts, setTs] = useState(0);
  const [zones, setZones] = useState<Zone[]>([]);
  const [ok, setOk] = useState<boolean | null>(null);

  useEffect(() => {
    fetch(`${apiBase}/cameras/${cameraId}/zones`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setZones)
      .catch(() => setZones([]));
  }, [cameraId, apiBase]);

  useEffect(() => {
    setOk(null);
    setTs(Date.now());
    const id = setInterval(() => setTs(Date.now()), Math.max(1000, intervalMs));
    return () => clearInterval(id);
  }, [cameraId, intervalMs]);

  return (
    <div style={{ position: 'relative', background: '#000', borderRadius: 8, overflow: 'hidden', lineHeight: 0, maxWidth: 640 }}>
      <img
        src={snapshotUrl(cameraId, ts, apiBase)}
        alt="live camera"
        style={{ display: 'block', width: '100%', borderRadius: 8 }}
        draggable={false}
        onLoad={() => setOk(true)}
        onError={() => setOk(false)}
      />
      {ok && zones.length > 0 && (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          {zones.map((zn, i) => {
            const colour = RULE_COLOUR[zn.rule];
            const poly = zonePoly(zn)
              .map((p) => `${p.x * 100},${p.y * 100}`)
              .join(' ');
            return (
              <polygon
                key={i}
                points={poly}
                fill={`color-mix(in srgb, ${colour} 20%, transparent)`}
                stroke={colour}
                strokeWidth={2}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        </svg>
      )}
      {ok &&
        zones.map((zn, i) => {
          const bb = bboxOf(zonePoly(zn));
          return (
            <span
              key={i}
              style={{ position: 'absolute', left: `${bb.x * 100}%`, top: `calc(${bb.y * 100}% - 18px)`, background: RULE_COLOUR[zn.rule], color: '#fff', fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap', pointerEvents: 'none' }}
            >
              {zn.name} · {RULE_LABEL[zn.rule]}
            </span>
          );
        })}
      {ok === false && (
        <div style={{ padding: 16, color: 'var(--text-dim)', fontSize: 13, lineHeight: 1.5 }}>
          No live frame — check the camera connection.
        </div>
      )}
    </div>
  );
}
